import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Button, Form, Input, message, Select, Space, Typography } from "antd";
import { ExportFile } from "assets/icons";
import Layout from "components/layout";
import { bindActionCreators } from "redux";
import { DatePicker } from "screens/private/report/components/datePicker";
import { Creators as LocationActions } from "store/location/location-duck";
import { IStructuresAllData } from "store/location/location-model";
import { Creators as ReportActions } from "store/report/report-duck";
import { IMovementItems } from "store/report/report-model";
import { Creators as UserSessionActions } from "store/user-session/user-session-duck";
import { IUserData, MenuItemActive } from "store/user-session/user-session-model";
import TokenService from "utils/auth/Token.service";
import { rulesPlates, validationSpecialCharacters } from "utils/form/validations";
import { Roles, maxRangeDate, validationCharacters } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import { getOptions, rangeConfig, translateDatePicker } from "../components/datePicker/configs";
import * as S from "./../components/styled";
import MovementsReportGrid from "./grid";
import ColumnsTable from "./grid/columnsTable";
import { onConfirmExportMovementReport, onConfirmQueryMovementReport } from "./movements.service";

const { RangePicker } = DatePicker;

interface IPropsScreenMoviments {
  isLoadingMovements?: boolean;
  isLoadingMovementsRegisters?: boolean;
  structuresAllList?: IStructuresAllData[];
  locationByStructureIdList?: IStructuresAllData[];
  locationStructure?: IStructuresAllData;
  movementItems?: Array<IMovementItems>;
  movementTotalItems?: number;
  pageIndex?: number;
  itemsByPage?: number;
  userData?: IUserData;
}

interface IDispatchProps {
  locationStructuresAllFetchAsync?: (...args) => void;
  locationByStructureIdFetchAsync?: (...args) => void;
  locationStructureByLocationFetchAsync?: (...args) => void;
  reportMovementChangeFieldValue?: (...args) => void;
  reportMovementFetchAsync?: (...args) => void;
  reportMovementsFetchRegisterAsync?: (...args) => void;
  reportMovementFetchRegister?: (...args) => void;
  userSessionMenuItemActive?: (...args) => void;
}

export type IProps = IPropsScreenMoviments & IDispatchProps;

const Movements = ({
  isLoadingMovements,
  isLoadingMovementsRegisters,
  userData,
  structuresAllList,
  locationByStructureIdList,
  locationStructure,
  movementItems,
  movementTotalItems,
  pageIndex,
  itemsByPage,
  locationStructuresAllFetchAsync,
  locationByStructureIdFetchAsync,
  locationStructureByLocationFetchAsync,
  reportMovementChangeFieldValue,
  reportMovementFetchAsync,
  reportMovementsFetchRegisterAsync,
  reportMovementFetchRegister,
  userSessionMenuItemActive,
}: IProps) => {
  const tokenService = new TokenService();
  const [form] = Form.useForm();
  const FILL_IN_THE_FIELDS_CORRECTLY_MESSAGE = useTranslation({ id: "fill_in_the_fields_correctly" });
  const TITLE_PAGE = useTranslation({ id: "movements" });
  const MAXIMUM_PERIOD_TO_SELECT = useTranslation({ id: "maximum_period_to_select" });
  const [structureSelected, setStructureSelected] = useState(null);
  const [value, setValue] = useState([userData?.location]);
  const [allowView, setAllowView] = useState(false);
  const [structureDefault, setStructureDefault] = useState(locationStructure);
  const [getStructure, setGetStructure] = useState(false);
  const [dates, setDates] = useState([]);
  const [dateValue, setDateValue] = useState([]);
  const maxDaysSelect = 31;
  const { Option } = Select;
  const { Title } = Typography;
  const structureSelectedId = structureSelected === null ? locationStructure?.id : structureSelected?.id;

  const locationStructureByLocationFetchAsyncCallback = useCallback((location) => locationStructureByLocationFetchAsync(location), [locationStructureByLocationFetchAsync]);
  const locationStructuresAllFetchAsyncCallback = useCallback(() => locationStructuresAllFetchAsync(), [locationStructuresAllFetchAsync]);
  const locationByStructureIdFetchAsyncCallback = useCallback((id) => locationByStructureIdFetchAsync(id), [locationByStructureIdFetchAsync]);

  const permissionView = () => {
    const allowedRoles = [Roles.ADMIN, Roles.CONTROL, Roles.CONCIERGE_LEADER, Roles.ATTENDANCE, Roles.CONCIERGE_ATTENDANCE];
    const allow = (Object.values(allowedRoles) as string[]).includes(userData.role);
    return setAllowView(!allow);
  };

  useEffect(() => {
    userSessionMenuItemActive(MenuItemActive.MOVEMENTS);
    tokenService.authenticateToken();
  }, []);

  useEffect(() => {
    return () => {
      reportMovementFetchRegister([], 0);
    };
  }, []);

  useEffect(() => {
    locationStructuresAllFetchAsyncCallback();
  }, []);

  useEffect(() => {
    if (userData?.location) {
      permissionView();
      locationStructureByLocationFetchAsyncCallback(userData.locationId);
      setGetStructure(true);
    }
  }, []);

  useEffect(() => {
    if (structureDefault) {
      locationByStructureIdFetchAsyncCallback(structureDefault.id);
    }
  }, [structureDefault]);

  useEffect(() => {
    if (getStructure) {
      setStructureDefault(locationStructure);
      setStructureSelected(locationStructure);
    }
  }, []);

  const onChangeStructureSelect = (structure) => {
    setValue([]);
    const objStructure = JSON.parse(structure);
    form.setFieldsValue({ structure: objStructure.description });
    locationByStructureIdFetchAsyncCallback(objStructure.id);
    setStructureSelected(objStructure);
  };

  const onOpenChangeDatePicker = (open) => {
    if (open) {
      setDates([]);
      form.setFieldsValue({ rangePicker: "" });
    }
  };

  const resetStateFilters = () => {
    reportMovementChangeFieldValue("structureId", null);
    reportMovementChangeFieldValue("initialEventTime", null);
    reportMovementChangeFieldValue("finalEventTime", null);
    reportMovementChangeFieldValue("location", null);
    reportMovementChangeFieldValue("pageIndex", 1);
    reportMovementChangeFieldValue("plate", null);
    reportMovementChangeFieldValue("trailerPlate", null);
    reportMovementChangeFieldValue("dtDocument", null);
  };

  const onExportMovement = async () => {
    onConfirmExportMovementReport({
      form,
      locationStructure: structureSelectedId,
      locationByStructureIdList,
      location: value,
      userData,
      message,
      messageDescription: FILL_IN_THE_FIELDS_CORRECTLY_MESSAGE,
      reportMovementFetchAsync,
    });
    return true;
  };

  const onReportQuery = async () => {
    resetStateFilters();
    onConfirmQueryMovementReport({
      form,
      locationStructure: structureSelectedId,
      locationByStructureIdList,
      location: value,
      userData,
      pageIndex,
      itemsByPage,
      message,
      messageDescription: FILL_IN_THE_FIELDS_CORRECTLY_MESSAGE,
      reportMovementsFetchRegisterAsync,
    });
    return true;
  };

  return (
    <Layout title={TITLE_PAGE} isLoading={isLoadingMovements} role={userData.role}>
      <Form onFinish={onExportMovement} form={form} initialValues={{ structure: locationStructure?.description }}>
        <S.ReportContainer>
          <S.FilterContainerPrimary>
            <S.SelectStructureContainer>
              <Title level={5} style={S.ReportTitle}>
                {useTranslation({ id: "structure" })}
              </Title>
              <Form.Item name="structure">
                <Select
                  size="large"
                  placeholder={locationStructure?.description}
                  style={S.StructureSelect}
                  disabled={allowView}
                  onChange={(structure) => onChangeStructureSelect(structure)}
                  data-testid="structure-select-option"
                >
                  {structuresAllList.length > 1 &&
                    structuresAllList.map((option, index) => (
                      <Option key={`select-options-structure-${index}`} value={JSON.stringify(option)}>
                        {option.description}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </S.SelectStructureContainer>
            <S.LocationsContainer>
              <Title style={S.ReportTitle} level={5}>
                {useTranslation({ id: "unit" })}
              </Title>
              <Form.Item name="location">
                <Space style={S.LocationSelect} direction="vertical">
                  <Select
                    mode="multiple"
                    placeholder={useTranslation({ id: "all" })}
                    style={{ width: "100%" }}
                    value={value}
                    onChange={(newValue: string[]) => {
                      setValue(newValue);
                    }}
                    disabled={allowView}
                    maxTagCount="responsive"
                    data-testid="location-select-option"
                  >
                    {locationByStructureIdList.length > 0 &&
                      locationByStructureIdList.map((item, index) => (
                        <Option key={`select-options-location-${index}`} value={item.description}>
                          {item.description}
                        </Option>
                      ))}
                  </Select>
                </Space>
              </Form.Item>
            </S.LocationsContainer>
            <S.FilterDocumentContainer>
              <Title level={5} style={S.ReportTitle}>
                {useTranslation({ id: "transport_document" })}
              </Title>
              <S.FormItem name="dtDocument" data-testid="filter-document">
                <Input allowClear style={S.InputFields} maxLength={10} />
              </S.FormItem>
            </S.FilterDocumentContainer>
          </S.FilterContainerPrimary>
          <S.FilterReportContainerSecondary>
            <S.FilterPlateContainer>
              <Title level={5} style={S.ReportTitle}>
                {useTranslation({ id: "plate" })}
              </Title>
              <S.FormItem name="plate" data-testid="filter-plate" rules={rulesPlates()}>
                <Input
                  style={S.InputFields}
                  className="inputUpper"
                  maxLength={7}
                  allowClear
                  onKeyPress={(e) => {
                    validationCharacters(e, validationSpecialCharacters);
                  }}
                />
              </S.FormItem>
            </S.FilterPlateContainer>
            <S.FilterPlateContainer>
              <Title level={5} style={S.ReportTitle}>
                {useTranslation({ id: "trailers_plates" })}
              </Title>
              <S.FormItem data-testid="filter-trailer-plate" name="trailerPlate" rules={rulesPlates()}>
                <Input
                  style={S.InputFields}
                  className="inputUpper"
                  allowClear
                  maxLength={7}
                  onKeyPress={(e) => {
                    validationCharacters(e, validationSpecialCharacters);
                  }}
                />
              </S.FormItem>
            </S.FilterPlateContainer>
            <S.DatePickerContainer>
              <Title level={5} style={S.ReportTitle}>
                {useTranslation({ id: "date" })}
              </Title>
              <Space direction="vertical" size={12} style={S.SpaceDatePicker}>
                <Form.Item name="rangePicker" {...rangeConfig()} data-testid="datePickerSubmit">
                  <RangePicker
                    size="large"
                    format="DD/MM/YYYY"
                    placeholder={[useTranslation({ id: "start_date" }), useTranslation({ id: "end_date" })]}
                    disabledDate={(current) => maxRangeDate(current, dates, maxDaysSelect)}
                    value={(dateValue[0], dateValue[1])}
                    ranges={getOptions()}
                    onCalendarChange={(val) => setDates(val)}
                    onChange={(val) => setDateValue(val)}
                    onOpenChange={onOpenChangeDatePicker}
                    allowClear={false}
                    renderExtraFooter={() => MAXIMUM_PERIOD_TO_SELECT}
                    data-testid="creationDate"
                    locale={translateDatePicker()}
                  ></RangePicker>
                </Form.Item>
              </Space>
            </S.DatePickerContainer>
            <S.ButtonSubmitFilterContainer>
              <Button className="primaryButton" size="large" onClick={onReportQuery} disabled={isLoadingMovementsRegisters} data-testid="submit-request">
                {useTranslation({ id: "filter" })}
              </Button>
            </S.ButtonSubmitFilterContainer>
            <S.ButtonSubmitExportContainer>
              <Button className="secondaryButton" htmlType="submit" size="large" disabled={isLoadingMovementsRegisters} data-testid="submit-export" icon={<ExportFile />}>
                {useTranslation({ id: "export" })}
              </Button>
            </S.ButtonSubmitExportContainer>
          </S.FilterReportContainerSecondary>
        </S.ReportContainer>
      </Form>
      <MovementsReportGrid
        columnsTable={ColumnsTable()}
        movementItems={movementItems}
        movementTotalItems={movementTotalItems}
        pageIndex={pageIndex}
        isLoadingMovementsRegisters={isLoadingMovementsRegisters}
        reportMovementChangeFieldValue={reportMovementChangeFieldValue}
        reportMovementsFetchRegisterAsync={reportMovementsFetchRegisterAsync}
      />
    </Layout>
  );
};

const mapDispatchToActions = (dispatch: any) => bindActionCreators({ ...UserSessionActions, ...ReportActions, ...LocationActions }, dispatch);

const mapStateToProps = (state) => ({
  isLoadingMovements: state.Report.isLoadingMovements,
  isLoadingMovementsRegisters: state.Report.isLoadingMovementsRegisters,
  userData: state.UserSession.userData,
  structuresAllList: state.Location.structuresAllList,
  locationByStructureIdList: state.Location.locationByStructureIdList,
  locationStructure: state.Location.locationStructure,
  movementItems: state.Report.movementItems,
  movementTotalItems: state.Report.movementTotalItems,
  pageIndex: state.Report.movementFilter.pageIndex,
  itemsByPage: state.Report.movementFilter.itemsByPage,
});

export default connect(mapStateToProps, mapDispatchToActions)(Movements);
